// oxlint-disable complexity
// ABOUTME: Shows aggregate processing results for RMC requests after order submission.
// It lets users refresh statuses, reload confirmed orders, or dismiss terminal results.
import {Alert, Button} from '@hm/fundamental'
import {mdiChevronDown, mdiChevronUp} from '@mdi/js'
import {CircularProgress} from '@mui/material'
import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {useDismissFailedOrderRequest} from '../../../../../common/react-query/hooks/mutations/useDismissFailedOrderRequest'
import {useRmcOrderStatuses} from '../../hooks/useRmcOrderStatuses'
import {TransactionResultType} from '../../types/confirmationResult'
import {RmcOrderRequestSummary} from '../../types/order'

import {RmcOrderStatusDetails} from './RmcOrderStatusDetails'

interface RmcOrderStatusBannerProps {
  requests: RmcOrderRequestSummary[]
  onReload: () => void
  onDismiss: () => void
}

interface StatusCounts {
  success: number
  pending: number
  failure: number
  pendingDispatch: number
  customerActionRequired: number
}

const getStatusCounts = (statuses: Array<{status: TransactionResultType}>): StatusCounts =>
  statuses.reduce<StatusCounts>((counts, {status}) => ({...counts, [status]: counts[status] + 1}), {
    success: 0,
    pending: 0,
    failure: 0,
    pendingDispatch: 0,
    customerActionRequired: 0
  })

export const RmcOrderStatusBanner: React.FC<RmcOrderStatusBannerProps> = ({
  requests,
  onReload,
  onDismiss
}) => {
  const {t} = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const requestIds = requests.map(({requestId}) => requestId)
  const {statuses, isChecking, isError, checkStatuses} = useRmcOrderStatuses(requestIds)
  const {mutateAsync: dismissFailedOrderRequest} = useDismissFailedOrderRequest()

  if (requests.length === 0) return null

  const counts = getStatusCounts(statuses)
  const hasFailure = counts.failure > 0
  const hasCustomerActionRequired = counts.customerActionRequired > 0
  const hasPending = counts.pending > 0
  const hasPendingDispatch = counts.pendingDispatch > 0
  const hasSuccess = counts.success > 0
  const hasReloadableStatus = hasSuccess || hasPendingDispatch || hasCustomerActionRequired

  let variant: 'info' | 'warning' | 'error' = 'info'
  let titleKey = 'orderIntake.rmc.statusBanner.checkingTitle'
  let action: 'check' | 'reload' | 'dismiss' | undefined

  if (isError) {
    variant = 'error'
    titleKey = 'orderIntake.rmc.statusBanner.checkErrorTitle'
    action = 'check'
  } else if (!isChecking && hasReloadableStatus) {
    variant = hasFailure || hasCustomerActionRequired || hasPendingDispatch ? 'warning' : 'info'
    titleKey = hasCustomerActionRequired
      ? 'orderIntake.rmc.statusBanner.actionRequiredTitle'
      : hasPendingDispatch
        ? 'orderIntake.rmc.statusBanner.pendingTitle'
        : hasFailure
          ? 'orderIntake.rmc.statusBanner.failureTitle'
          : 'orderIntake.rmc.statusBanner.successTitle'
    action = 'reload'
  } else if (!isChecking && hasPending) {
    variant = 'warning'
    titleKey = 'orderIntake.rmc.statusBanner.pendingTitle'
    action = 'check'
  } else if (!isChecking && hasFailure) {
    variant = 'error'
    titleKey = 'orderIntake.rmc.statusBanner.failureTitle'
    action = 'dismiss'
  }

  const handleAction = async () => {
    if (action === 'check') {
      await checkStatuses()
      return
    }

    if (action === 'reload') {
      onReload()
      return
    }

    const failedRequestIds = statuses
      .filter(({status}) => status === 'failure')
      .map(({requestId}) => requestId)

    await Promise.all(failedRequestIds.map((requestId) => dismissFailedOrderRequest({requestId})))
    onDismiss()
  }

  const actionLabel =
    action === 'check'
      ? t('orderIntake.rmc.confirmation.checkStatusButton')
      : action === 'reload'
        ? t('reload')
        : t('dismiss')

  return (
    <Alert
      variant={variant}
      className="[&>*:last-child]:flex-1"
      data-test-id="rmc-order-status-banner"
    >
      <div className="w-full" aria-live="polite">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold">{t(titleKey, {count: requests.length})}</p>
            {statuses.length > 0 && (
              <p className="text-sm">
                {t('orderIntake.rmc.statusBanner.summary', {
                  confirmedCount: counts.success,
                  pendingCount: counts.pending,
                  pendingDispatchCount: counts.pendingDispatch,
                  actionRequiredCount: counts.customerActionRequired,
                  failedCount: counts.failure
                })}
              </p>
            )}
          </div>
          <div className="flex shrink-0 gap-2">
            <Button
              variant="tertiary"
              icon={isExpanded ? mdiChevronUp : mdiChevronDown}
              iconPosition="right"
              onClick={() => setIsExpanded((current) => !current)}
              aria-expanded={isExpanded}
              aria-controls="rmc-order-status-details"
            >
              {t(
                isExpanded
                  ? 'orderIntake.rmc.statusBanner.showLess'
                  : 'orderIntake.rmc.statusBanner.showMore'
              )}
            </Button>
            {isChecking ? (
              <CircularProgress
                size={24}
                aria-label={t('orderIntake.rmc.confirmation.statusHeaders.checking')}
                data-test-id="rmc-order-status-check-spinner"
              />
            ) : action ? (
              <Button variant="primary" onClick={() => void handleAction()}>
                {actionLabel}
              </Button>
            ) : null}
          </div>
        </div>
        {isExpanded && (
          <RmcOrderStatusDetails
            requests={requests}
            statuses={statuses}
            isChecking={isChecking}
            isError={isError}
          />
        )}
      </div>
    </Alert>
  )
}
